import { z } from "zod";

/**
 * Shared DTO cho Admin User Directory & Detail (US-AE3.F1-0010 / @UC9020 directory · @UC9021 detail).
 * Một nguồn sự thật cho api (S01 — query + redaction theo vai) và admin web (S03 — render),
 * tránh drift shape ở 2 nơi (DRY).
 *
 * BẢO MẬT (invariants):
 * - C2: field PII (email/phone/fullName/avatarUrl/ip/deviceInfo) NULLABLE+OPTIONAL — backend
 *   redact theo vai (MASK) hoặc placeholder (anonymized → null). Contract CHỈ cho phép vắng/null,
 *   KHÔNG ép lộ.
 * - C3: schema `.strict()` — KHÔNG khai báo passwordHash/secret/balanceMinor; field lạ bị reject
 *   tại biên (chống vô tình leak field nhạy cảm / field tiền — story này không hiển thị tiền).
 * - Cursor opaque string — đối xứng common/pagination/cursor.
 *
 * Envelope khớp quy ước list repo: `{ data, pageInfo: { hasNext, nextCursor } }`
 * (common/pagination/cursor.ts · admin-search). KHÔNG dùng {items, nextCursor}.
 *
 * Limit: default 20 (backend áp khi vắng), max 50. q OPTIONAL (vắng ⇒ list toàn bộ theo cursor).
 */

/** Max page-size. Default (20) do backend áp khi vắng. */
export const ADMIN_USER_DIRECTORY_MAX_LIMIT = 50;

/** Độ dài tối đa của `q` — chặn input phình to tại biên. 256 dư cho email/uuid/tên/SĐT. */
export const ADMIN_USER_DIRECTORY_MAX_QUERY_LENGTH = 256;

/**
 * Trạng thái user toàn hệ. `suspended` thêm ở US-AE3.F3-0011 (migration users.status); contract
 * chấp nhận sẵn để UI render đủ 3 trạng thái khi F3 merge — KHÔNG bịa giá trị ở backend khi chưa có.
 */
export const adminUserStatusSchema = z.enum(["active", "suspended", "anonymized"]);

export type AdminUserStatus = z.infer<typeof adminUserStatusSchema>;

/**
 * Query list: `q` OPTIONAL (email/SĐT/tên, ≤256), cursor opaque optional, limit ∈ [1,50] optional.
 * Vắng `q` ⇒ liệt kê toàn bộ (cursor). Tái dùng style admin-search nhưng q không bắt buộc.
 */
export const adminUserDirectoryQuerySchema = z.object({
  q: z.string().max(ADMIN_USER_DIRECTORY_MAX_QUERY_LENGTH).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(ADMIN_USER_DIRECTORY_MAX_LIMIT).optional(),
});

export type AdminUserDirectoryQuery = z.infer<typeof adminUserDirectoryQuerySchema>;

/**
 * List item (UC9020). email/phone/fullName NULLABLE+OPTIONAL (redaction MASK → vắng;
 * anonymized → null). KHÔNG bao giờ chứa password/secret/tiền (`.strict()`).
 */
export const adminUserDirectoryItemSchema = z
  .object({
    id: z.string(),
    status: adminUserStatusSchema,
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    fullName: z.string().nullable().optional(),
    createdAt: z.string(),
    groupCount: z.number().int().min(0),
    lastLoginAt: z.string().nullable().optional(),
  })
  .strict();

export type AdminUserDirectoryItem = z.infer<typeof adminUserDirectoryItemSchema>;

/** Envelope khớp quy ước repo `{ data, pageInfo }` (cursor opaque, hasNext). */
export const adminUserDirectoryResponseSchema = z.object({
  data: z.array(adminUserDirectoryItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type AdminUserDirectoryResponse = z.infer<typeof adminUserDirectoryResponseSchema>;

/** Social account liên kết (read-only, không secret). */
const adminUserSocialAccountSchema = z
  .object({
    provider: z.string(),
    providerUid: z.string(),
    createdAt: z.string(),
  })
  .strict();

/** Tư cách thành viên nhóm (group + role + status). */
const adminUserMembershipSchema = z
  .object({
    groupId: z.string(),
    groupName: z.string().nullable().optional(),
    role: z.string(),
    status: z.string(),
  })
  .strict();

/** Phiên đăng nhập (KHÔNG chứa refreshTokenHash — chỉ metadata). */
const adminUserSessionSchema = z
  .object({
    id: z.string(),
    deviceInfo: z.string().nullable().optional(),
    ip: z.string().nullable().optional(),
    createdAt: z.string(),
    revokedAt: z.string().nullable().optional(),
    expiresAt: z.string().nullable().optional(),
  })
  .strict();

/** Audit liên quan user (đã redact diff ở DB; ở đây chỉ metadata). */
const adminUserAuditSchema = z
  .object({
    id: z.string(),
    action: z.string(),
    entity: z.string().nullable().optional(),
    entityId: z.string().nullable().optional(),
    createdAt: z.string(),
  })
  .strict();

/**
 * Hồ sơ 360° (UC9021). PII NULLABLE+OPTIONAL (redaction/anonymized). Nested mảng BẮT BUỘC có
 * (rỗng khi không có). KHÔNG field tiền/secret (`.strict()`).
 */
export const adminUserDetailSchema = z
  .object({
    id: z.string(),
    status: adminUserStatusSchema,
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    fullName: z.string().nullable().optional(),
    avatarUrl: z.string().nullable().optional(),
    createdAt: z.string(),
    socialAccounts: z.array(adminUserSocialAccountSchema),
    memberships: z.array(adminUserMembershipSchema),
    sessions: z.array(adminUserSessionSchema),
    recentAudit: z.array(adminUserAuditSchema),
  })
  .strict();

export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>;
export type AdminUserSocialAccount = z.infer<typeof adminUserSocialAccountSchema>;
export type AdminUserMembership = z.infer<typeof adminUserMembershipSchema>;
export type AdminUserSession = z.infer<typeof adminUserSessionSchema>;
export type AdminUserAudit = z.infer<typeof adminUserAuditSchema>;
